// Company-local time. The sim runs in REAL time in the company's timezone; all
// stored timestamps are UTC ms epoch, and "local" is only for schedules,
// day keys and narration.
export const COMPANY_TZ = 'America/Chicago';

export type LocalParts = { year: number; month: number; day: number; hour: number; minute: number; dow: number };

const fmt = new Intl.DateTimeFormat('en-US', {
  timeZone: COMPANY_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  weekday: 'short',
});
const DOW: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

export function localParts(ms: number): LocalParts {
  const parts: Record<string, string> = {};
  for (const p of fmt.formatToParts(new Date(ms))) parts[p.type] = p.value;
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    dow: DOW[parts.weekday ?? 'Sun'] ?? 0,
  };
}

/** 'YYYY-MM-DD' in company-local time — the budget/day key. */
export function dayKey(ms: number): string {
  const p = localParts(ms);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/** Minutes since local midnight. */
export function localMinuteOfDay(ms: number): number {
  const p = localParts(ms);
  return p.hour * 60 + p.minute;
}

/** 'HH:MM' → minutes since midnight. */
export function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/** True when local time-of-day is inside [start, end) — handles windows across midnight. */
export function inLocalWindow(ms: number, startHHMM: string, endHHMM: string): boolean {
  const t = localMinuteOfDay(ms);
  const a = hhmmToMinutes(startHHMM);
  const b = hhmmToMinutes(endHHMM);
  return a <= b ? t >= a && t < b : t >= a || t < b;
}
